import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api, { formatApiError } from "@/lib/api";
import { formatTRY, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Printer, Check as CheckIcon, Loader2 } from "lucide-react";
import UnitAttachments from "@/components/UnitAttachments";
import MoneyInput from "@/components/MoneyInput";

export function DepositDialog({ open, onOpenChange, unit, onDone }) {
  const [form, setForm] = useState({
    buyer_name: "", buyer_tc: "", buyer_phone: "", buyer_email: "", buyer_address: "",
    amount: 0, date: todayISO(), note: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && unit) {
      setForm({
        buyer_name: unit.buyer_name || "",
        buyer_tc: unit.buyer_tc || "",
        buyer_phone: unit.buyer_phone || "",
        buyer_email: unit.buyer_email || "",
        buyer_address: unit.buyer_address || "",
        amount: unit.deposit_amount || 0,
        date: todayISO(),
        note: "",
      });
    }
  }, [open, unit]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/units/${unit.id}/deposit`, { ...form, amount: Number(form.amount) });
      toast.success("Kapora alındı ve daire rezerve edildi");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!unit) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="deposit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Kapora Al · Daire {unit.unit_no}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Alıcı Ad Soyad</Label>
              <Input data-testid="deposit-buyer-name" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} />
            </div>
            <div className="space-y-2"><Label>TC Kimlik No</Label>
              <Input data-testid="deposit-buyer-tc" value={form.buyer_tc} onChange={(e) => setForm({ ...form, buyer_tc: e.target.value })} maxLength={11} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Telefon</Label>
              <Input value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} />
            </div>
            <div className="space-y-2"><Label>E-posta</Label>
              <Input value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2"><Label>Adres</Label>
            <Textarea value={form.buyer_address} onChange={(e) => setForm({ ...form, buyer_address: e.target.value })} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3 p-3 bg-secondary/50 rounded-md border border-border">
            <div className="space-y-2"><Label>Kapora Tutarı (₺)</Label>
              <MoneyInput data-testid="deposit-amount" value={form.amount} onChange={(n) => setForm({ ...form, amount: n })} />
            </div>
            <div className="space-y-2"><Label>Tarih</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2"><Label>Not</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Opsiyonel" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button data-testid="deposit-confirm" onClick={submit} disabled={!form.buyer_name || !form.amount || loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Kaporayı Al
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ContractDialog({ open, onOpenChange, unit, onDone }) {
  const [step, setStep] = useState("form"); // "form" | "preview"
  const [contractId, setContractId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (open && unit) {
      // Check existing contract for this unit — prefer signed one, else latest draft
      (async () => {
        const { data } = await api.get(`/contracts?unit_id=${unit.id}`);
        const signed = data.find((c) => c.status === "imzalandi");
        const draft = data.find((c) => c.status === "taslak");
        const existing = signed || draft;
        if (existing) {
          setContractId(existing.id);
          setForm(existing);
          if (signed) {
            // Signed: go directly to preview with frozen HTML
            const { data: htmlData } = await api.get(`/contracts/${existing.id}/html`);
            setPreviewHtml(htmlData.contract.generated_html || htmlData.html);
            setStep("preview");
            return;
          }
        } else {
          setContractId(null);
          setForm({
            unit_id: unit.id,
            buyer_name: unit.buyer_name || "",
            buyer_tc: unit.buyer_tc || "",
            buyer_phone: unit.buyer_phone || "",
            buyer_email: unit.buyer_email || "",
            buyer_address: unit.buyer_address || "",
            sale_price: unit.price || 0,
            deposit_amount: unit.deposit_amount || 0,
            payment_type: "vadeli",
            down_payment: 0,
            installment_count: 24,
            first_due_date: todayISO(),
            bank: "",
            delivery_date: "",
            contract_date: todayISO(),
            penalty_amount: 0,
            extra_clauses: "",
          });
        }
        setStep("form");
      })();
    }
  }, [open, unit]);

  const saveDraft = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        sale_price: Number(form.sale_price),
        deposit_amount: Number(form.deposit_amount),
        down_payment: Number(form.down_payment),
        installment_count: Number(form.installment_count),
        penalty_amount: Number(form.penalty_amount),
      };
      let id = contractId;
      if (id) {
        await api.patch(`/contracts/${id}`, payload);
      } else {
        const { data } = await api.post("/contracts", payload);
        id = data.id;
        setContractId(id);
      }
      const { data: htmlData } = await api.get(`/contracts/${id}/html`);
      setPreviewHtml(htmlData.html);
      setStep("preview");
      toast.success("Sözleşme taslağı kaydedildi");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const printContract = () => {
    if (!contractId) return;
    // Sync open (user gesture) with same-origin URL → cookie auth sends automatically.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/contracts/${contractId}/pdf`;
    window.open(url, "_blank", "noopener");
  };

  const signContract = async () => {
    if (!window.confirm("Sözleşmeyi imzalı olarak kaydetmek istediğinize emin misiniz? Bu işlem daireyi 'Satıldı' durumuna geçirir ve vadeli ise taksit çeklerini oluşturur.")) return;
    setLoading(true);
    try {
      await api.post(`/contracts/${contractId}/sign`);
      toast.success("Sözleşme imzalandı, satış tamamlandı");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!unit || !form) return null;

  const remaining =
    Number(form.sale_price) - Number(form.deposit_amount) - (form.payment_type === "vadeli" ? Number(form.down_payment) : 0);
  const monthly =
    form.payment_type === "vadeli" && Number(form.installment_count) > 0 ? remaining / Number(form.installment_count) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {step === "form" ? "Sözleşme Bilgileri" : "Sözleşme Önizleme"} · Daire {unit.unit_no}
          </DialogTitle>
        </DialogHeader>

        {step === "form" ? (
          <div className="space-y-4">
            <section className="p-4 rounded-md border border-border">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-3">Alıcı Bilgileri</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Ad Soyad</Label>
                  <Input data-testid="contract-buyer-name" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} />
                </div>
                <div className="space-y-2"><Label>TC Kimlik</Label>
                  <Input value={form.buyer_tc} onChange={(e) => setForm({ ...form, buyer_tc: e.target.value })} maxLength={11} />
                </div>
                <div className="space-y-2"><Label>Telefon</Label>
                  <Input value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} />
                </div>
                <div className="space-y-2"><Label>E-posta</Label>
                  <Input value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} />
                </div>
                <div className="space-y-2 col-span-2"><Label>Adres</Label>
                  <Textarea value={form.buyer_address} onChange={(e) => setForm({ ...form, buyer_address: e.target.value })} rows={2} />
                </div>
              </div>
            </section>

            <section className="p-4 rounded-md border border-border">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-3">Satış & Ödeme</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Satış Bedeli</Label>
                  <MoneyInput data-testid="contract-sale-price" value={form.sale_price} onChange={(n) => setForm({ ...form, sale_price: n })} />
                </div>
                <div className="space-y-2"><Label>Kapora (Alındı)</Label>
                  <MoneyInput value={form.deposit_amount} onChange={(n) => setForm({ ...form, deposit_amount: n })} />
                </div>
                <div className="space-y-2"><Label>Ödeme Türü</Label>
                  <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                    <SelectTrigger data-testid="contract-payment-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pesin">Peşin</SelectItem>
                      <SelectItem value="vadeli">Vadeli</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.payment_type === "vadeli" && (
                  <>
                    <div className="space-y-2"><Label>Peşinat</Label>
                      <MoneyInput value={form.down_payment} onChange={(n) => setForm({ ...form, down_payment: n })} />
                    </div>
                    <div className="space-y-2"><Label>Taksit Sayısı</Label>
                      <Input type="number" value={form.installment_count} onChange={(e) => setForm({ ...form, installment_count: e.target.value })} />
                    </div>
                    <div className="space-y-2"><Label>İlk Vade</Label>
                      <Input type="date" value={form.first_due_date || ""} onChange={(e) => setForm({ ...form, first_due_date: e.target.value })} />
                    </div>
                    <div className="space-y-2 col-span-2"><Label>Banka</Label>
                      <Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} />
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground p-2 bg-secondary rounded">
                      Kalan bedel: <span className="tabular-nums font-medium text-foreground">{formatTRY(remaining)}</span> · Aylık taksit ≈ <span className="tabular-nums font-medium text-foreground">{formatTRY(monthly)}</span>
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className="p-4 rounded-md border border-border">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-3">Sözleşme & Teslim</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Sözleşme Tarihi</Label>
                  <Input type="date" value={form.contract_date || ""} onChange={(e) => setForm({ ...form, contract_date: e.target.value })} />
                </div>
                <div className="space-y-2"><Label>Teslim Tarihi</Label>
                  <Input type="date" value={form.delivery_date || ""} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />
                </div>
                <div className="space-y-2 col-span-2"><Label>Cayma Bedeli (₺)</Label>
                  <MoneyInput value={form.penalty_amount} onChange={(n) => setForm({ ...form, penalty_amount: n })} />
                </div>
                <div className="space-y-2 col-span-2"><Label>Özel Şartlar</Label>
                  <Textarea value={form.extra_clauses} onChange={(e) => setForm({ ...form, extra_clauses: e.target.value })} rows={3} placeholder="Opsiyonel - Ek maddeler" />
                </div>
              </div>
            </section>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
              <FileText className="w-4 h-4" />
              <span>Standart Gayrimenkul Satış Vaadi Sözleşmesi</span>
              {form?.contract_no && (
                <span data-testid="contract-no-badge" className="ml-auto px-2 py-1 rounded bg-brand/15 text-brand text-[10px] uppercase tracking-widest font-semibold tabular-nums">
                  {form.contract_no}
                </span>
              )}
              {form?.status === "imzalandi" && (
                <span className="px-2 py-1 rounded bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] text-[10px] uppercase tracking-widest font-semibold">
                  İmzalandı
                </span>
              )}
            </div>
            <div
              data-testid="contract-preview"
              className="border border-border rounded-md p-6 bg-white text-black max-h-[55vh] overflow-y-auto text-sm leading-relaxed contract-preview"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(previewHtml, {
                  USE_PROFILES: { html: true },
                  FORBID_TAGS: ["script", "style", "iframe", "object", "embed"],
                  FORBID_ATTR: ["onerror", "onload", "onclick"],
                }),
              }}
            />
            <UnitAttachments unit={unit} canEdit />
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {step === "form" ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
              <Button data-testid="contract-save-draft" onClick={saveDraft} disabled={!form.buyer_name || !form.sale_price || loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Kaydet ve Önizle
              </Button>
            </>
          ) : form?.status === "imzalandi" ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Kapat</Button>
              <Button data-testid="contract-pdf" onClick={printContract}>
                <Printer className="w-4 h-4 mr-2" /> PDF İndir
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep("form")}>← Düzenle</Button>
              <Button variant="outline" onClick={printContract} data-testid="contract-print">
                <Printer className="w-4 h-4 mr-2" /> Yazdır / PDF
              </Button>
              <Button data-testid="contract-sign" onClick={signContract} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckIcon className="w-4 h-4 mr-2" />}
                Sözleşmeyi İmzala
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Preview typography — light theme colors always for readability */
export const contractPreviewCSS = `
.contract-preview h1{font-family:'Cabinet Grotesk',sans-serif;font-size:20px;text-align:center;margin:0 0 8px;letter-spacing:-.01em;}
.contract-preview .meta{text-align:center;color:#78716c;margin:0 0 24px;font-size:12px;}
.contract-preview h3{font-family:'Cabinet Grotesk',sans-serif;font-size:14px;margin:20px 0 4px;letter-spacing:-.01em;}
.contract-preview p{margin:4px 0;text-align:justify;}
.contract-preview table{width:100%;border-collapse:collapse;margin:6px 0;font-size:13px;}
.contract-preview table th{text-align:left;padding:6px 10px;background:#f5f5f4;font-weight:600;width:170px;color:#1c1917;}
.contract-preview table td{padding:6px 10px;border-bottom:1px solid #e7e5e4;}
.contract-preview .signatures{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-top:40px;font-size:13px;}
`;
